import React from 'react';
import {
  Checkbox,
  Switch,
  FormControlLabel,
  Box,
  Grid,
} from '@material-ui/core';
import { withFormik, FormikProps } from 'formik';
import { IntlShape, injectIntl } from 'react-intl';
import * as Yup from 'yup';
import { translate } from 'helpers/intl';
import messages from 'containers/LoginRecoveryPage/SendLoginPage/messages';
import { ShortBody, LongBody } from 'components/Typography';
import InputField from 'components/InputField';
import {
  StyledBox,
  MemoIdBox,
  StyledCaptions,
  StyledSmallSectionHeader,
  StyledOrangeButton,
} from 'containers/LoginRecoveryPage/SendLoginPage/styles';
import { sanlamBlue } from 'Material-UI/colors';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { pushTo } from 'helpers/routes';
import PAGES from 'routes/pages';
import { EVENT_CATEGORIES } from 'helpers/GATracking';

const InnerForm = (
  props: {
    email: string;
    identifiant: string;
  } & FormikProps<FormValues>,
) => {
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = props;

  const handleOnClickSaveId = () => {
    !values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  };

  React.useEffect(() => {
    values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  }, [values.isSaveUserId]);

  React.useEffect(() => {
    values.email === '' && setFieldValue('inMyAddressMail', false);
  }, [values.email]);

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Box mb={3} px={[2, 4, 8]}>
          <StyledSmallSectionHeader color={sanlamBlue} weight="500">
            {values.identifiant}
          </StyledSmallSectionHeader>
          <MemoIdBox marginBottom={2}>
            <GaClickHOC
              category={EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN}
              label="Mémoriser mon identifiant"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    name="isSaveUserId"
                    value={values.isSaveUserId}
                    onChange={handleChange}
                    onClick={handleOnClickSaveId}
                    checked={values.isSaveUserId}
                    data-testid="is-save-user-id"
                  />
                }
                label={
                  <StyledCaptions>
                    {translate({ ...messages.memoId }, props)}
                  </StyledCaptions>
                }
              />
            </GaClickHOC>
          </MemoIdBox>

          {values.email !== '' && (
            <>
              <Box marginBottom={1.25}>
                <ShortBody>
                  {translate({ ...messages.iWantToSaveMyId }, props)}
                </ShortBody>
              </Box>
              <StyledBox px={3} py={0.75} isRadiusTop>
                <Grid container justify="space-between" alignItems="center">
                  <Grid xs={7} sm={6} item>
                    <LongBody weight="500">
                      {translate({ ...messages.inMyAddressMail }, props)}{' '}
                    </LongBody>
                  </Grid>
                  <Grid xs={3} sm={2} item>
                    <Box marginLeft={4}>
                      <GaClickHOC
                        category={
                          EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN
                        }
                        label="Sauvegarder par email"
                      >
                        <Switch
                          checked={values.inMyAddressMail}
                          onChange={handleChange}
                          name="inMyAddressMail"
                          color="primary"
                          data-testid="in-my-address-mail"
                        />
                      </GaClickHOC>
                    </Box>
                  </Grid>
                </Grid>
              </StyledBox>
              <StyledBox px={3} paddingTop={3} paddingBottom={1}>
                <Box marginBottom={3}>
                  <InputField
                    name="email"
                    placeholder={translate({ ...messages.email }, props)}
                    label={translate({ ...messages.email }, props)}
                    value={values.email}
                    error={errors.email && touched.email}
                    helperText={touched.email && errors.email}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    disabled
                  />
                </Box>
              </StyledBox>
            </>
          )}
          <Box marginBottom={2}></Box>
          <Grid>
            <GaClickHOC
              category={EVENT_CATEGORIES.FORGOTTEN_ACCESS_RECOVERY_LOGIN}
              label="Confirmer"
            >
              <StyledOrangeButton type="submit" variant="contained">
                <React.Fragment>
                  {translate({ ...messages.confirm }, props)}
                </React.Fragment>
              </StyledOrangeButton>
            </GaClickHOC>
          </Grid>
        </Box>
      </form>
    </React.Fragment>
  );
};

export interface FormProps {
  intl: IntlShape;
  email: string;
  onSubmitStep: Function;
  identifiant: string;
}

interface FormValues {
  inMyAddressMail: boolean;
  isSaveUserId: boolean;
  email: string;
  identifiant: string;
}

const IdMemo = withFormik<FormProps, FormValues>({
  mapPropsToValues: /* istanbul ignore next */ (props) => {
    return {
      isSaveUserId: true,
      inMyAddressMail: true,
      identifiant: props.identifiant,
      email: props.email,
    };
  },

  validationSchema: /* istanbul ignore next */ (props: any) =>
    Yup.object().shape({
      inMyAddressMail: Yup.boolean(),
      email: Yup.string()
        .email(translate({ ...messages.emailInputError }, props))
        .when('inMyAddressMail', {
          is: true,
          then: Yup.string().required(
            translate({ ...messages.requiredField }, props),
          ),
        }),
    }),

  handleSubmit: /* istanbul ignore next */ (values, { props }) => {
    const valuesToSend = {
      login: values.identifiant,
      inMyAddressMail: values.inMyAddressMail,
      email: values.email,
    };
    /* istanbul ignore next */
    values.email === ''
      ? pushTo(PAGES.LOGIN)
      : props.onSubmitStep(valuesToSend);
  },
})(InnerForm);

export default injectIntl(IdMemo);
